import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data || []);
      if (!active && data?.[0]) setActive(data[0]);
    } catch (_) {}
  };
  useEffect(() => { loadThreads(); }, []);

  const loadMessages = async (t) => {
    if (!t) return;
    try {
      const { data } = await api.get(`/messages/thread/${t.user_id}`);
      setMessages(data || []);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (_) {}
  };
  useEffect(() => { loadMessages(active); }, [active?.user_id]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { to_user_id: active.user_id, content: text });
      setText("");
      loadMessages(active);
      loadThreads();
    } catch (_) { toast.error("Send failed"); }
  };

  return (
    <div data-testid="messages-page" className="h-[calc(100vh-180px)] grid md:grid-cols-[300px_1fr] gap-4">
      <div className="card-surface overflow-hidden flex flex-col">
        <div className="p-4 border-b border-white/10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Threads</div>
          <div className="font-display text-2xl font-black uppercase tracking-tight mt-1">Messages</div>
        </div>
        <div className="overflow-auto flex-1">
          {threads.length === 0 && <div className="p-6 text-[#A3A3A3] text-sm">No conversations yet.</div>}
          {threads.map((t) => (
            <button
              key={t.user_id}
              onClick={() => setActive(t)}
              className={`w-full text-left p-4 border-b border-white/5 transition-colors ${active?.user_id === t.user_id ? "bg-white/5" : "hover:bg-white/[0.03]"}`}
              data-testid={`thread-${t.user_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-bold">{t.name}</div>
                {t.unread > 0 && <span className="text-[10px] bg-[#38BDF8] text-black font-black px-1.5" data-testid={`unread-${t.user_id}`}>{t.unread}</span>}
              </div>
              <div className="text-xs text-[#A3A3A3] truncate mt-1">{t.last?.content || "Say hi 👋"}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="card-surface flex flex-col overflow-hidden">
        {!active ? (
          <div className="m-auto text-[#A3A3A3] text-sm">Pick a conversation.</div>
        ) : (
          <>
            <div className="p-4 border-b border-white/10">
              <div className="font-display uppercase tracking-tight text-xl font-black">{active.name}</div>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-3" data-testid="messages-list">
              {messages.map((m) => {
                const mine = m.from_user_id === user.user_id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] p-3 text-sm ${mine ? "bg-[#38BDF8] text-black" : "bg-white/5 text-white"}`} data-testid={`message-${m.id}`}>
                      {m.content}
                      <div className={`text-[10px] mt-1 ${mine ? "text-black/60" : "text-[#A3A3A3]"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="p-4 border-t border-white/10 flex gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a message…"
                className="flex-1 bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none px-3 py-2 text-sm"
                data-testid="message-input"
              />
              <button type="submit" className="btn-primary px-4 py-2 text-xs" data-testid="message-send-button">
                <Send size={14} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
